import {CSSResult, html, LitElement, nothing, PropertyValues, TemplateResult, unsafeCSS,} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import '../price/price.js';
import {sharedCss} from '../../../scripts/style-bundle/shared.js';
import styles from './product-family-product.scss?inline';
import {ProductFamilyPrice, ProductFamilyProduct} from '../product-family/product-family.js';
import {ProductIdentifier} from '../../../scripts/service/product-identifier.js';
import {Messages, Price} from '../../../@types/wishlist.js';
import {consume} from '@lit/context';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';
import saleIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-12_sale.svg?element';
import {addTitleToIcon} from '../icon/icon-accessibility.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';

@customElement('ents-product-family-product')
export class ProductFamilyProductComponent extends LitElement {
  @consume({context: i18nContext})
  protected messages!: Messages;

  @consume({context: wishlistAppConfigContext})
  protected appConfig!: WishlistAppConfig;

  @property({
    type: Object,
    attribute: 'product',
  }) protected product!: ProductFamilyProduct;

  @property({type: Number, attribute: 'position'}) private position?: number = undefined;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    setTimeout(() => this.triggerCampEvent(), 0);
  }

  private triggerCampEvent() {
    document.dispatchEvent(new CustomEvent('camp:aktionsflags:initialized'));
  }

  protected render(): TemplateResult {
    const product = this.product;
    return html`
      <div class="ents-product-family-product-grid" data-test-ents-product-family="${product.code}">
        <a
          href="${product.url}"
          title="${product.brand} ${product.name}"
        >
          <div
            class="ents-product-family-product-container"
            title="${product.brand} ${product.name}"
            data-test-ents-product-family-picture
          >
            <picture>
              <source srcset="${product.view.standardUrl} 1x, ${product.view.standardRetinaUrl} 2x"/>
              <img src="${product.view.fallbackStandardUrl}"
                   alt="${product.brand} ${product.name}"/>
            </picture>
          </div>
          <div
            class="ents-product-family-product-infos"
            title="${product.brand} ${product.name}"
          >
            ${product.price.saleFlagContent ? this.renderReductionFlag() : nothing}
            ${this.renderCampaignFlag(product)}
            <span class="ents-product-family-product-infos-brand"
                  data-test-ents-product-family-brand>${product.brand}</span>
            <span class="ents-product-family-product-infos"
                  data-test-ents-product-family-name>${product.name}</span>
            <ents-price
              .price="${this.convertToPrice(product.price)}"
              data-test-ents-product-family-price
            ></ents-price>
          </div>
        </a>
        ${this.renderWishlistButton(product)}
      </div>
    `;
  }

  private renderReductionFlag(): TemplateResult {
    return html`
      <div class="ents-wishlist-flags">
        <div
          class="bdk-badge-article ents-wishlist-flags--item ents-wishlist-flags--item__sale"
          variant="sale"
          size="small"
          data-test-ents-product-family-reduction-flag>
          ${addTitleToIcon(saleIcon(), this.messages['ents_wishlist_filter_option_onSale'])}
        </div>
      </div>
    `;
  }

  private renderWishlistButton(product: ProductFamilyProduct): TemplateResult {
    const initialState = this.constructInitStateIfOnWishlist(product);
    return html`
      <ents-wishlist-button
        initial-state="[&quot;${initialState}&quot;]"
        placement="product-family-wishlist"
        syan="${product.code}"
        color-id="${product.color}"
        data-test-ents-product-family-wishlist-button
        position="${this.position}"
      >
      </ents-wishlist-button>
    `;
  }

  private renderCampaignFlag(product: ProductFamilyProduct) {
    return html`
      <camp-aktionsflag
        data-test-ents-product-family-campaign-flag
        syan="${product.code}"
        farb-id="${product.color}">
      </camp-aktionsflag>
    `;
  }

  private constructInitStateIfOnWishlist(product: ProductFamilyProduct) {
    if (product.isOnWishlist) {
      return ProductIdentifier.createByProductFamilyProduct(product).id;
    }
    return '';
  }

  private convertToPrice(productFamilyPrice: ProductFamilyPrice): Price {
    return {
      currentPriceMessage: productFamilyPrice.currentPrice,
      unitPriceMessage: productFamilyPrice.unitPrice,
      lowestPriceMessage: productFamilyPrice.lowestPrice,
      originalPriceMessage: productFamilyPrice.originalPrice,
      showOriginalPrice: productFamilyPrice.showOriginalPrice,
      strikeThrough: productFamilyPrice.strikeThroughOriginalAndLowestPrice,
      isReduced: productFamilyPrice.isReduced,
      saleFlagMessage: productFamilyPrice.saleFlagContent,
      // following field is irrelevant for the product family layer
      currentPriceInCent: 0,
    };
  }
}
